import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { UseOutletContextAdmin } from './Admin';
import { StateLanguage } from '../../../languages/config/StateLanguage';
import { ApiDigitalAuthPolicy } from '../../../services/auth/policy.service';
import { Policy } from '../../../models/policy.model';
import { errorWebHandler } from '../../../scripts/error.web.handler.script';

import AppPreloader from '../../../components/Preloader';

export interface PolicyListPageProps {};

const PolicyListPage: React.FunctionComponent<PolicyListPageProps> = props => {
  const {setRoute, appServiceForUser} = UseOutletContextAdmin()
  const {lang} = StateLanguage()

  const [mounted, setMounted] = useState(false)
  const [policies, setPolicies] = useState<Policy[] | null>(null)

  const loadPolicies = async (api_key: string) => {
    await ApiDigitalAuthPolicy.getPolicies(api_key, false).then((response) => {
      if (response.status === 200) {
        setPolicies(Array.isArray(response.data) ? response.data : [])
      } else {
        setPolicies([])
        errorWebHandler(lang, {response})
      }
    }).catch((error) => {
      errorWebHandler(lang, error)
      setPolicies([])
    })
  }

  useEffect(() => {
    if (appServiceForUser) {
      setPolicies(null)
      loadPolicies(appServiceForUser.api_key)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appServiceForUser])

  useEffect(() => {
    setMounted(true)
    setRoute({path: {root: lang.labels.administration, branch: lang.labels.policies}, company: true})

    return () => setMounted(false)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!mounted) return null

  return (
    <div className="w-100 h-100">
      { policies !== null
        ?
        <div className="card card-flush">
          <div className="card-header">
            <div className="card-title">
              <h3 className="fw-bolder">{lang.labels.policies}</h3>
            </div>
            <div className="card-toolbar">
              <Link to="/main/admin/policies/create" className="btn btn-sm btn-primary">{lang.labels.createPolicy}</Link>
            </div>
          </div>
          <div className="card-body pt-0">
            <div className="dataTables_wrapper">
              <div className="table-responsive">
                <table className="table align-middle table-row-dashed mb-2">
                  <thead className="border-bottom">
                    <tr className="text-start text-muted text-uppercase fw-bolder fs-7">
                      <th className="min-w-200px sorting">{lang.labels.name}</th>
                      <th className="min-w-200px sorting">{lang.labels.description}</th>
                      <th className="min-w-100px sorting">{lang.labels.statements}</th>
                      <th className="min-w-100px sorting">{lang.labels.type}</th>
                      <th className="w-75px sorting_disabled text-end">{lang.labels.actions}</th>
                    </tr>
                  </thead>
                  { policies.length > 0
                    ?
                    <tbody className="text-secondary fw-bold">
                      { policies.map((item, index) => (
                        <tr key={index} className="row-dashed">
                          <td>
                            <span className="fw-bolder text-dark">{item.name}</span>
                          </td>
                          <td>
                            <span className="text-muted">{item.description || lang.labels.none}</span>
                          </td>
                          <td>
                            <span className="badge badge-light fs-8">{(item.statements ?? []).length}</span>
                          </td>
                          <td>
                            { item.is_system
                              ? <span className="badge badge-light-info fs-8">{lang.labels.system}</span>
                              : <span className="badge badge-light fs-8">Custom</span>
                            }
                          </td>
                          <td>
                            <div className="d-flex justify-content-end">
                              <Link to={`/main/admin/policies/${item.id ?? (item as any)._id}`} className="btn btn-sm btn-icon btn-light-primary">
                                <span className="svg-icon svg-icon-3">
                                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M17.5 11H6.5C4 11 2 9 2 6.5C2 4 4 2 6.5 2H17.5C20 2 22 4 22 6.5C22 9 20 11 17.5 11ZM15 6.5C15 7.9 16.1 9 17.5 9C18.9 9 20 7.9 20 6.5C20 5.1 18.9 4 17.5 4C16.1 4 15 5.1 15 6.5Z" fill="black"/>
                                    <path opacity="0.3" d="M17.5 22H6.5C4 22 2 20 2 17.5C2 15 4 13 6.5 13H17.5C20 13 22 15 22 17.5C22 20 20 22 17.5 22ZM6.5 15C5.1 15 4 16.1 4 17.5C4 18.9 5.1 20 6.5 20C7.9 20 9 18.9 9 17.5C9 16.1 7.9 15 6.5 15Z" fill="black"/>
                                  </svg>
                                </span>
                              </Link>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    :
                    <tbody>
                      <tr className="text-secondary fw-bold">
                        <td colSpan={5} className="text-center">{lang.labels.noPolicies}</td>
                      </tr>
                    </tbody>
                  }
                </table>
              </div>
            </div>
          </div>
        </div>
        :
        <AppPreloader></AppPreloader>
      }
    </div>
  )
};

export default PolicyListPage;
